// Shared building blocks of the admin console (user management, data
// sources, assistants, instances). Theme-aware styles: UserManagement.css.

import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faTrash, faChevronDown, faAnglesUp, faEquals, faAnglesDown, faShieldHalved, faCrown,
  faEye, faEyeSlash, faWandMagicSparkles, faCircleCheck, faTriangleExclamation, faCircleXmark, faCircle,
} from '@fortawesome/free-solid-svg-icons';

export const deepClone = (o) => JSON.parse(JSON.stringify(o));
export const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;
/** The real name only — '' when none is set (the username is shown instead). */
export const realName = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ');

/** Avatar letters from the USERNAME: several accounts share one real name
 *  (memo, memo1, memo2 are all "Memo Dener"), so the login name is what
 *  tells them apart — memo1 → M1, patrick_mh → PM, robert → RO. */
export function userInitials(username) {
  const u = (username || '').trim();
  if (!u) return '?';
  const numbered = u.match(/^([A-Za-z]+)[._-]?(\d{1,2})\d*$/);
  if (numbered) return (numbered[1][0] + numbered[2]).toUpperCase();
  const parts = u.split(/[._@\-\s]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return u.slice(0, 2).toUpperCase();
}

// Stable colour per username, so the same account always looks the same.
const AVATAR_HUES = [190, 262, 330, 28, 145, 212, 48, 0, 170, 292];
export function avatarHue(username) {
  let h = 0;
  for (const ch of String(username || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length];
}

/** Natural order: memo2 before memo10. */
export const naturalCompare = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

export const EMPTY_USER = {
  first_name: '', last_name: '', email: '', username: '', status: 'active', language: 'de', role: 'user',
  llm_config: { llm_model: '', bot_function_ids: [], system_prompt: '', temperature: 0.7, agent_model: '' },
  ui_config: {
    home_instance: '', data_sources: {}, show_breaches_button: false, rm_name: '', ppi_consolidation: '', ppi_portfolio: '', card_source: '',
    expandables: [], hero_image: '', are_sources: [], are_excluded_sources: [], are_screener_enabled: true,
  },
};

export const PRIORITIES = [
  { value: 'high', label: 'High', icon: faAnglesUp, color: '#e06b6b' },
  { value: 'medium', label: 'Medium', icon: faEquals, color: '#e0b15a' },
  { value: 'low', label: 'Low', icon: faAnglesDown, color: '#5ec07a' },
];

// Which section of the user page each server-side field lives in — an error
// scrolls there.
export const FIELD_SECTION = {
  first_name: 'profile', last_name: 'profile', email: 'profile', language: 'profile', username: 'profile',
  role: 'access', status: 'access', password: 'access', current_password: 'access',
  'ui_config.expandables': 'modules', 'ui_config.hero_image': 'modules', 'ui_config.show_breaches_button': 'modules',
  'ui_config.rm_name': 'data', 'ui_config.ppi_consolidation': 'data', 'ui_config.ppi_portfolio': 'data',
  'ui_config.card_source': 'data', 'ui_config.data_sources': 'data', 'ui_config.home_instance': 'access',
  'llm_config.llm_model': 'ai', 'llm_config.temperature': 'ai', 'llm_config.system_prompt': 'ai',
  'llm_config.bot_function_ids': 'ai', 'llm_config.agent_model': 'llm',
  'ui_config.are_sources': 'are', 'ui_config.are_excluded_sources': 'are', 'ui_config.are_screener_enabled': 'are',
};

// The data-scope fields: what an empty value means, shown so nobody grants
// "everything" by accident.
export const SCOPE_FIELDS = {
  rm_name: {
    label: 'Advisor', hint: 'EFD relationship manager whose book the user sees.',
    empty: 'whole bank', placeholder: 'Empty = whole bank · e.g. Bodmer, Sinan',
  },
  ppi_consolidation: {
    label: 'Consolidation', hint: 'Limits the user to one consolidation (client).',
    empty: 'whole survista tenant', placeholder: 'Empty = whole tenant · e.g. Juve',
  },
  ppi_portfolio: {
    label: 'Portfolio', hint: 'Narrower than the consolidation and wins over it.',
    empty: 'no portfolio limit', placeholder: 'Optional · e.g. 0206-00351978-0001',
  },
};

export function relativeTime(value) {
  if (!value) return 'never';
  const then = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(then.getTime())) return '—';
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) { const m = Math.floor(days / 30); return `${m} month${m === 1 ? '' : 's'} ago`; }
  const y = Math.floor(days / 365);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

export function formatDate(value, withTime = true) {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '—';
  return withTime
    ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export const toTime = (value) => {
  const t = value ? new Date(String(value).replace(' ', 'T')).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

export function generatePassword(length = 14) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789-_!?';
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

// ─────────────────────────────────────────────────────────
// Small building blocks
// ─────────────────────────────────────────────────────────

export function Field({ label, hint, error, wide, children }) {
  return (
    <div className={`um-field${wide ? ' wide' : ''}${error ? ' has-error' : ''}`}>
      {label && <label className="um-label">{label}</label>}
      {children}
      {typeof error === 'string' && <div className="um-hint error">{error}</div>}
      {hint && <div className="um-hint">{hint}</div>}
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled }) {
  return (
    <label className={`um-switch${disabled ? ' disabled' : ''}`}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="um-switch-track" />
      {label && <span className="um-switch-label">{label}</span>}
    </label>
  );
}

export function RoleBadge({ role }) {
  if (role === 'superadmin') return <span className="um-badge um-badge-super"><FontAwesomeIcon icon={faCrown} /> Super admin</span>;
  return role === 'admin'
    ? <span className="um-badge um-badge-admin"><FontAwesomeIcon icon={faShieldHalved} /> Admin</span>
    : <span className="um-badge um-badge-user">User</span>;
}

export function StatusPill({ status }) {
  const active = (status || 'active') !== 'inactive';
  return <span className={`um-status ${active ? 'on' : 'off'}`}><i />{active ? 'Active' : 'Inactive'}</span>;
}

export function Avatar({ username, size = 'md' }) {
  return (
    <span className={`um-avatar um-avatar-${size}`} style={{ '--h': avatarHue(username) }} aria-hidden="true">
      {userInitials(username)}
    </span>
  );
}

/** A titled card of the user page; `id` is the anchor the side nav jumps to. */
export function Card({ id, title, description, actions, children, tone }) {
  return (
    <section id={id ? `um-sec-${id}` : undefined} className={`um-card${tone ? ` ${tone}` : ''}`}>
      <header className="um-card-head">
        <div className="um-card-head-text">
          <h2 className="um-card-title">{title}</h2>
          {description && <p className="um-card-desc">{description}</p>}
        </div>
        {actions && <div className="um-card-actions">{actions}</div>}
      </header>
      {children}
    </section>
  );
}

/** One setting: label (+ hint) on the left, the control on the right.
 *  `stack` puts the control under the label (long lists, text areas). */
export function Row({ label, hint, error, stack, children }) {
  return (
    <div className={`um-set${stack ? ' stack' : ''}${error ? ' has-error' : ''}`}>
      <div className="um-set-label">
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </div>
      <div className="um-set-control">
        {children}
        {typeof error === 'string' && <div className="um-hint error">{error}</div>}
      </div>
    </div>
  );
}

export function PasswordInput({ value, onChange, placeholder, autoComplete, onGenerate }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="um-input-group">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button type="button" className="um-icon-btn" onClick={() => setVisible((v) => !v)} title={visible ? 'Hide' : 'Show'}>
        <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
      </button>
      {onGenerate && (
        <button type="button" className="um-icon-btn" onClick={() => { onGenerate(); setVisible(true); }} title="Generate a strong password">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
        </button>
      )}
    </div>
  );
}

// Custom priority picker — a native <select> cannot render the level icons.
// The menu is position:fixed (anchored to the button) to escape overflow clips.
export function PrioritySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const current = PRIORITIES.find((p) => p.value === value) || PRIORITIES[1];

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    setOpen(true);
  };

  return (
    <div className="um-prio">
      <button ref={btnRef} type="button" className="um-prio-btn" aria-haspopup="listbox" aria-expanded={open} onClick={toggle}>
        <FontAwesomeIcon icon={current.icon} style={{ color: current.color }} />
        <span>{current.label}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`um-prio-caret${open ? ' open' : ''}`} />
      </button>
      {open && coords && (
        <ul ref={menuRef} className="um-prio-menu" role="listbox" style={{ top: coords.top, left: coords.left, width: coords.width }}>
          {PRIORITIES.map((p) => (
            <li key={p.value} role="option" aria-selected={p.value === value}
              className={p.value === value ? 'selected' : ''}
              onClick={() => { onChange(p.value); setOpen(false); }}>
              <FontAwesomeIcon icon={p.icon} style={{ color: p.color }} /> {p.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SourceList({ items, onChange, withPriority, labelPlaceholder, urlPlaceholder, addLabel, danger }) {
  const update = (idx, key, val) => onChange(items.map((s, i) => (i === idx ? { ...s, [key]: val } : s)));
  return (
    <div className="um-sources">
      {items.length === 0 && <div className="um-empty-inline">None yet.</div>}
      {items.map((s, idx) => (
        <div key={idx} className="um-source-row">
          <input className="um-source-label" placeholder={labelPlaceholder} value={s.label || ''} onChange={(e) => update(idx, 'label', e.target.value)} />
          <input className="um-source-url" placeholder={urlPlaceholder} value={s.url || ''} onChange={(e) => update(idx, 'url', e.target.value)} />
          {withPriority && <PrioritySelect value={s.priority || 'medium'} onChange={(v) => update(idx, 'priority', v)} />}
          <button type="button" className="um-icon-btn danger" title="Remove" onClick={() => onChange(items.filter((_, i) => i !== idx))}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ))}
      <button type="button" className={`um-add-btn${danger ? ' danger' : ''}`}
        onClick={() => onChange([...items, withPriority ? { label: '', url: '', priority: 'medium' } : { label: '', url: '' }])}>
        <FontAwesomeIcon icon={faPlus} /> {addLabel}
      </button>
    </div>
  );
}

export function ConfirmDialog({ title, text, confirmLabel, danger, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="um-overlay" onClick={onCancel}>
      <div className="um-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{text}</p>
        <div className="um-dialog-btns">
          <button type="button" className="um-btn um-btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className={`um-btn ${danger ? 'um-btn-danger' : 'um-btn-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Check results (connection tests, data checks) ──
const STATUS_ICON = { ok: faCircleCheck, warn: faTriangleExclamation, fail: faCircleXmark };

export function StatusIcon({ status }) {
  return <FontAwesomeIcon icon={STATUS_ICON[status] || faCircle} className={`um-st um-st-${status || 'none'}`} />;
}

/** rows: [{label|name, status, detail}] */
export function CheckRows({ rows }) {
  return (
    <ul className="um-check-rows">
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i} className={`um-check-row ${r.status}`}>
          <StatusIcon status={r.status} />
          <span className="um-check-name">{r.label || r.name}</span>
          <span className="um-check-detail">{r.detail}{r.ms !== undefined ? ` · ${r.ms} ms` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

export function Spinner({ small }) {
  return <div className={`um-spinner${small ? ' small' : ''}`} />;
}
